import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { usePageSeo } from "@/hooks/usePageSeo";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Calculator, Download, FileText, Clock, Users, TrendingUp, AlertCircle } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ToolContext } from "@/components/tools/ToolContext";
import { Link } from "react-router-dom";

type Language = 'es' | 'en';

const WordCounterPage = () => {
  const { language, url, esUrl, enUrl, onLanguageChange } = usePageSeo();
  const [text, setText] = useState("");
  const [analysisGenerated, setAnalysisGenerated] = useState(false);


  const content = {
    es: {
      title: "Contador de Palabras Avanzado - Análisis Completo de Texto",
      description: "Herramienta profesional para contar palabras, caracteres y analizar la legibilidad de tu contenido con métricas avanzadas y reporte detallado.",
      toolTitle: "Contador de Palabras con IA",
      toolDescription: "Análisis completo de texto con métricas de legibilidad, SEO y engagement",
      placeholder: "Pega o escribe tu contenido aquí para obtener un análisis completo...",
      stats: {
        words: "Palabras",
        characters: "Caracteres",
        charactersNoSpaces: "Sin espacios",
        paragraphs: "Párrafos",
        sentences: "Oraciones",
        readingTime: "Tiempo de lectura",
        speakingTime: "Tiempo de locución",
        minutes: "min",
        readability: "Legibilidad"
      },
      analysis: {
        title: "Análisis de Contenido",
        readabilityScore: "Puntuación de Legibilidad",
        seoScore: "Puntuación SEO", 
        engagementScore: "Puntuación de Engagement",
        excellent: "Excelente",
        good: "Bueno",
        fair: "Regular",
        poor: "Necesita mejora",
        recommendations: "Recomendaciones",
        downloadReport: "Descargar Reporte PDF"
      },
      recommendations: {
        shortParagraphs: "Usa párrafos más cortos para mejor legibilidad",
        longerContent: "El contenido es muy corto, considera expandirlo",
        goodLength: "Longitud ideal para el engagement",
        addSubheadings: "Agrega subtítulos para estructurar mejor",
        varyingSentences: "Varía la longitud de las oraciones",
        keywordDensity: "Optimiza la densidad de palabras clave"
      }
    },
    en: {
      title: "Advanced Word Counter - Complete Text Analysis",
      description: "Professional tool to count words, characters and analyze readability of your content with advanced metrics and detailed report.",
      toolTitle: "AI Word Counter",
      toolDescription: "Complete text analysis with readability, SEO and engagement metrics",
      placeholder: "Paste or write your content here to get a complete analysis...",
      stats: {
        words: "Words",
        characters: "Characters", 
        charactersNoSpaces: "Without spaces",
        paragraphs: "Paragraphs",
        sentences: "Sentences",
        readingTime: "Reading time",
        speakingTime: "Speaking time",
        minutes: "min",
        readability: "Readability"
      },
      analysis: {
        title: "Content Analysis",
        readabilityScore: "Readability Score",
        seoScore: "SEO Score",
        engagementScore: "Engagement Score", 
        excellent: "Excellent",
        good: "Good",
        fair: "Fair",
        poor: "Needs improvement",
        recommendations: "Recommendations",
        downloadReport: "Download PDF Report"
      },
      recommendations: {
        shortParagraphs: "Use shorter paragraphs for better readability",
        longerContent: "Content is too short, consider expanding it",
        goodLength: "Ideal length for engagement",
        addSubheadings: "Add subheadings for better structure",
        varyingSentences: "Vary sentence length",
        keywordDensity: "Optimize keyword density"
      }
    }
  };

  const t = content[language];

  // Calculations
  const getWordCount = () => text.trim() ? text.trim().split(/\s+/).length : 0;
  const getCharCount = () => text.length;
  const getCharCountNoSpaces = () => text.replace(/\s/g, '').length;
  const getParagraphCount = () => text.split(/\n\s*\n/).filter(p => p.trim()).length;
  const getSentenceCount = () => text.split(/[.!?]+/).filter(s => s.trim()).length;
  const getReadingTime = () => Math.ceil(getWordCount() / 200);
  const getSpeakingTime = () => Math.ceil(getWordCount() / 130);

  // Advanced analysis
  const getReadabilityScore = () => {
    const words = getWordCount();
    const sentences = getSentenceCount();
    const avgWordsPerSentence = words / sentences || 0;
    
    if (avgWordsPerSentence < 15) return { score: 90, level: t.analysis.excellent };
    if (avgWordsPerSentence < 20) return { score: 75, level: t.analysis.good };
    if (avgWordsPerSentence < 25) return { score: 60, level: t.analysis.fair };
    return { score: 40, level: t.analysis.poor };
  };

  const getSEOScore = () => {
    const words = getWordCount();
    const paragraphs = getParagraphCount();
    
    let score = 50;
    if (words >= 300 && words <= 2000) score += 30;
    if (paragraphs >= 3) score += 20;
    
    return Math.min(100, score);
  };

  const getEngagementScore = () => {
    const words = getWordCount();
    const readingTime = getReadingTime();
    
    let score = 50;
    if (readingTime >= 1 && readingTime <= 10) score += 30;
    if (words >= 500) score += 20;
    
    return Math.min(100, score);
  };

  const getRecommendations = () => {
    const recommendations = [];
    const words = getWordCount();
    const paragraphs = getParagraphCount();
    const avgWordsPerParagraph = words / paragraphs || 0;
    
    if (avgWordsPerParagraph > 100) recommendations.push(t.recommendations.shortParagraphs);
    if (words < 300) recommendations.push(t.recommendations.longerContent);
    if (words >= 500 && words <= 1500) recommendations.push(t.recommendations.goodLength);
    if (paragraphs < 3 && words > 300) recommendations.push(t.recommendations.addSubheadings);
    
    return recommendations;
  };

  const generateReport = () => {
    setAnalysisGenerated(true);
    // In a real implementation, this would generate and download a PDF
    console.log("Generating comprehensive report...");
  };

  const readability = getReadabilityScore();
  const seoScore = getSEOScore();
  const engagementScore = getEngagementScore();
  const recommendations = getRecommendations();

  const generateEmailResults = (): string => {
    if (!text.trim()) return '';
    
    return `
      <h2>Análisis Completo de Texto - ${new Date().toLocaleDateString('es-ES')}</h2>
      
      <h3>📊 Métricas Básicas</h3>
      <ul>
        <li><strong>Palabras:</strong> ${getWordCount()}</li>
        <li><strong>Caracteres:</strong> ${getCharCount()}</li>
        <li><strong>Caracteres sin espacios:</strong> ${getCharCountNoSpaces()}</li>
        <li><strong>Párrafos:</strong> ${getParagraphCount()}</li>
        <li><strong>Oraciones:</strong> ${getSentenceCount()}</li>
      </ul>
      
      <h3>🎯 Análisis SEO</h3>
      <ul>
        <li><strong>Puntuación SEO:</strong> ${seoScore}/100</li>
        <li><strong>Legibilidad:</strong> ${readability.score}/100 (${readability.level})</li>
        <li><strong>Engagement:</strong> ${engagementScore}/100</li>
      </ul>
      
      <h3>💡 Recomendaciones</h3>
      <ul>
        ${recommendations.map(r => `<li>${r}</li>`).join('')}
      </ul>
      
      <hr>
      <p><em>Análisis generado por Luis ROC - Herramientas SEO Gratuitas</em></p>
    `;
  };

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{t.title}</title>
        <meta name="description" content={t.description} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t.title} />
        <meta property="og:description" content={t.description} />
        <meta property="og:url" content={url} />
        <meta name="keywords" content="contador palabras, análisis texto, legibilidad, SEO, caracteres" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={onLanguageChange} />
        
        <main className="section-padding">
          <div className="container-custom">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
              <Link to={language === 'es' ? "/herramientas-seo" : "/en/seo-tools"} className="hover:text-primary transition-colors">
                {language === 'es' ? 'Herramientas SEO' : 'SEO Tools'}
              </Link>
              <span>/</span>
              <span>{language === 'es' ? 'Contador de palabras' : 'Word counter'}</span>
            </nav>


            {/* Header */}
            <div className="text-center max-w-4xl mx-auto mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Calculator className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-3xl lg:text-5xl font-bold heading-gradient">
                  {t.toolTitle}
                </h1>
              </div>
              <p className="text-xl text-muted-foreground">
                {t.toolDescription}
              </p>
            </div>

            {/* Main Tool */}
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <Card className="card-elegant">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      {language === 'es' ? 'Análisis de texto' : 'Text analysis'}
                    </CardTitle>
                    <CardDescription>
                      {language === 'es'
                        ? 'Pega tu contenido y obtén métricas avanzadas al instante'
                        : 'Paste your content and get detailed metrics instantly'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder={t.placeholder}
                      className="min-h-[300px] text-base leading-relaxed"
                    />
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                {/* Basic Stats */}
                <Card className="card-elegant">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg">{language === "es" ? "Estadísticas básicas" : "Basic statistics"}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-primary">{getWordCount()}</div>
                        <div className="text-xs text-muted-foreground">{t.stats.words}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-primary">{getCharCount()}</div>
                        <div className="text-xs text-muted-foreground">{t.stats.characters}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold">{getCharCountNoSpaces()}</div>
                        <div className="text-xs text-muted-foreground">{t.stats.charactersNoSpaces}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold">{getParagraphCount()}</div>
                        <div className="text-xs text-muted-foreground">{t.stats.paragraphs}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold">{getSentenceCount()}</div>
                        <div className="text-xs text-muted-foreground">{t.stats.sentences}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold flex items-center justify-center gap-1">
                          <Clock className="w-4 h-4" />
                          {getReadingTime()}
                        </div>
                        <div className="text-xs text-muted-foreground">{t.stats.readingTime} ({t.stats.minutes})</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Advanced Analysis */}
                {text && (
                  <Card className="card-elegant">
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" />
                        {t.analysis.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium">{t.analysis.readabilityScore}</span>
                            <Badge variant={readability.score > 70 ? "default" : "secondary"}>
                              {readability.level}
                            </Badge>
                          </div>
                          <Progress value={readability.score} className="h-2" />
                          <div className="text-xs text-muted-foreground mt-1">{readability.score}/100</div>
                        </div>

                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium">{t.analysis.seoScore}</span>
                            <span className="text-sm font-semibold">{seoScore}/100</span>
                          </div>
                          <Progress value={seoScore} className="h-2" />
                        </div>

                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium">{t.analysis.engagementScore}</span>
                            <span className="text-sm font-semibold">{engagementScore}/100</span>
                          </div>
                          <Progress value={engagementScore} className="h-2" />
                        </div>
                      </div>

                      <Button 
                        onClick={generateReport}
                        className="w-full bg-gradient-to-r from-primary to-primary/80"
                        disabled={!text.trim()}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {t.analysis.downloadReport}
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Recommendations */}
                {recommendations.length > 0 && (
                  <Card className="card-elegant border-orange-200 dark:border-orange-900">
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center gap-2 text-orange-600 dark:text-orange-400">
                        <AlertCircle className="w-5 h-5" />
                        {t.analysis.recommendations}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {recommendations.map((rec, index) => (
                          <li key={index} className="text-sm flex items-start gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 flex-shrink-0"></div>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
          <div className="max-w-4xl mx-auto">
            <ToolContext tool="word-counter" language={language} />
          </div>
        </main>
        
        <Footer language={language} />
      </div>
    </>
  );
};

export default WordCounterPage;